// Global Instructions Rule Applied!
// Read shape of GET /funds/:id/terms (view__fund_terms): raw funds__* term /
// commitment / management-fee columns + resolved prog__effective_* dates.
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

export const FundTermsRecordSchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string(),
    // Identity columns (Details section + New Fund form, 2026-09-09).
    funds__legal_name: z.string().nullish(),
    funds__functional_currency_id: z.string().nullish(),
    // Year-end MONTH (1-12).
    funds__year_end: z.number().nullish(),
    funds__managed_by_us: z.boolean().nullish(),
    funds__is_sbic: z.boolean().nullish(),
    // Partners' share of fund results (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md
    // 4.5): read-only on the Terms tab until PR 4's conversion; null = Static.
    funds__pnl_allocation_basis_id: z.number().nullish(),
    prog__pnl_allocation_basis_label: z.string().nullish(),
    funds__managed_by: z.string().nullish(),
    funds__pipeline_origination: z.string().nullish(),

    funds__formation_date: z.string().nullish(),
    funds__initial_closing_date: z.string().nullish(),
    funds__initial_contribution_date: z.string().nullish(),

    funds__final_closing_term: z.number().nullish(),
    funds__final_closing_term_units_id: z.number().nullish(),
    funds__final_closing_anchor_id: z.number().nullish(),
    funds__final_closing_anchor_date: z.string().nullish(),
    funds__final_closing_date_override: z.string().nullish(),
    funds__extended_final_closing_date: z.string().nullish(),

    funds__investment_period_term: z.number().nullish(),
    funds__investment_period_term_units_id: z.number().nullish(),
    funds__investment_period_anchor_id: z.number().nullish(),
    funds__investment_period_anchor_date: z.string().nullish(),
    funds__investment_period_date_override: z.string().nullish(),
    funds__extended_investment_period_end_date: z.string().nullish(),

    funds__base_term: z.number().nullish(),
    funds__base_term_units_id: z.number().nullish(),
    funds__base_term_anchor_id: z.number().nullish(),
    funds__base_term_anchor_date: z.string().nullish(),
    funds__termination_date: z.string().nullish(),

    funds__max_commitments: z.number().nullish(),
    funds__max_includes_gp: z.boolean().nullish(),
    funds__min_gp_commit_perc: z.number().nullish(),
    funds__min_gp_commit_base_id: z.number().nullish(),
    funds__min_gp_commit_amount: z.number().nullish(),
    funds__commitment_includes_expenses: z.boolean().nullish(),
    funds__commitment_includes_mgmt_fees: z.boolean().nullish(),

    funds__mgmt_fee_rate: z.number().nullish(),
    funds__mgmt_fee_basis_id: z.number().nullish(),
    // 'Regulatory capital + assumed leverage' fee-basis input (SBIC) — legacy
    // static amount, superseded by the gearing ratio below
    funds__mgmt_fee_assumed_leverage: z.number().nullish(),
    // SBIC: Assumed Leverage = Regulatory Capital × gearing ratio; the prog__
    // keys are today's computed values (fund__regulatory_capital_at), null on
    // funds that are not SBIC
    funds__sbic_gearing_ratio: z.number().nullish(),
    // SBIC: the licence date behind the distribution engine's twelve-months
    // gate (SBIC LPA §7.01(a); docs/WATERFALL_CARRY_DESIGN.md phase 3)
    funds__sbic_license_date: z.string().nullish(),
    // the fund's carried interest terms as the LPA states them: the default
    // every commitment without a side-letter term inherits (wf__lp_terms;
    // the Terms tab's Carried Interest section)
    funds__base_carry_rate: z.number().nullish(),
    funds__base_hurdle_multiple: z.number().nullish(),
    funds__stepped_carry_rate: z.number().nullish(),
    funds__stepped_hurdle_multiple: z.number().nullish(),
    prog__regulatory_capital: z.number().nullish(),
    prog__assumed_leverage: z.number().nullish(),
    prog__sbic_uncategorised_investors: z.number().nullish(),
    funds__fee_schedule_anchor_id: z.number().nullish(),
    funds__fee_schedule_anchor_date: z.string().nullish(),
    funds__late_closer_policy_id: z.number().nullish(),
    // Fee capital tracking (spec A3.8): the switch + optional start date; the
    // prog__ key is the resolved start (tracked-from date, else the first
    // issued capital call — earliest across a consolidated fund; null = not
    // tracked)
    funds__fee_capital_tracking: z.boolean().nullish(),
    funds__fee_capital_tracked_from: z.string().nullish(),
    prog__fee_capital_tracking_start: z.string().nullish(),
    // Fee break treatment (spec A3.9): the setting, the rule that applies (a
    // consolidated member follows its primary) and, on a non-primary member,
    // the primary fund it follows
    funds__fee_break_treatment_id: z.number().nullish(),
    prog__fee_break_treatment_effective_id: z.number().nullish(),
    prog__fee_break_in_full: z.boolean().nullish(),
    prog__fee_break_treatment_primary_fund: z.string().nullish(),
    // spec A3.9 addendum part 1: how the fund settles its reduced-fee
    // partners' waived fees (cfg.waived_fee_settlements; null = None). The
    // fund's own term — not inherited from a consolidated fund's primary
    funds__waived_fee_settlement_id: z.number().nullish(),
    // the switch the settlement replaced: a backend / RPC body older than
    // the addendum still emits it, and this schema is strict — tolerated,
    // never read
    funds__waived_fees_repaid: z.boolean().nullish(),
    // Fee period switch timing (cfg.fee_period_switch_timings): null = the
    // next fee period applies from its term start; a deferring timing moves
    // it to the start of the next billing period
    funds__fee_period_switch_timing_id: z.number().nullish(),
    // spec A3.9 addendum part 3: on the 'Regulatory capital + assumed
    // leverage' basis the fee on Assumed Leverage is charged at the full rate
    // whatever the side letter; true = side-letter rates apply to it too.
    // prog__has_regulatory_capital_basis: the headline basis or a fee period
    // uses that basis (the Terms tab shows the switch only then)
    funds__side_letter_rate_on_leverage: z.boolean().nullish(),
    prog__has_regulatory_capital_basis: z.boolean().nullish(),
    // capital call terms (capital call notices): the general partner, the
    // fund's address and what the LPA says about calls — the sections an
    // initial contribution / a drawdown / an expense contribution is called
    // under and the notice periods. Printed on each LP's notice.
    funds__general_partner_name: z.string().nullish(),
    funds__address: z.string().nullish(),
    funds__lpa_agreement_name: z.string().nullish(),
    funds__lpa_initial_contribution_clause: z.string().nullish(),
    funds__lpa_drawdown_clause: z.string().nullish(),
    funds__lpa_expense_clause: z.string().nullish(),
    funds__call_notice_days_initial: z.number().nullish(),
    funds__call_notice_days_drawdown: z.number().nullish(),
    funds__call_notice_business_days: z.boolean().nullish(),
    // the sender of the fund's notices: signatory and contact in one
    funds__call_notice_sender_name: z.string().nullish(),
    funds__call_notice_sender_title: z.string().nullish(),
    funds__call_notice_sender_phone: z.string().nullish(),
    funds__call_notice_sender_email: z.string().nullish(),
    // sequenced fee periods (fund_fee_periods rows)
    fee_periods: z.array(
      z
        .object({
          id: z.string(),
          sequence: z.number(),
          term: z.number().nullish(),
          term_units_id: z.number().nullish(),
          base_fee_rate: z.number().nullish(),
          basis_id: z.number().nullish(),
          reduction_scheme_id: z.number().nullish(),
          reduction_rate: z.number().nullish(),
          reduction_frequency_quarters: z.number().nullish(),
          fee_floor_rate: z.number().nullish(),
          // first day the period is billed: its term start, or the next
          // billing-period start under a deferred switch (read-only)
          prog__applies_from: z.string().nullish()
        })
        .strict()
    ),
    funds__mgmt_fee_frequency_id: z.number().nullish(),
    funds__mgmt_fee_day_count_id: z.number().nullish(),

    prog__effective_final_closing_date: z.string().nullish(),
    prog__effective_investment_period_start: z.string().nullish(),
    prog__effective_investment_period_end: z.string().nullish(),
    prog__effective_termination_date: z.string().nullish(),
    prog__effective_fee_schedule_start: z.string().nullish(),
    // last day the fee periods cover; null on a flat-rate fund. No fee
    // accrues after it (fee__schedule_end).
    prog__effective_fee_schedule_end: z.string().nullish(),
    prog__effective_dates_error: z.string().nullish(),

    // Fund lifecycle status (fund status, 2026-09-04). Two independent,
    // human-owned clocks. The `__is_*` flags come from cfg.fund_statuses and
    // drive the Tag colour — never the status NAME.
    funds__fundraising_status_id: z.string().nullish(),
    funds__fundraising_status__name: z.string().nullish(),
    funds__fundraising_status__is_open: z.boolean().nullish(),
    funds__fundraising_status__is_cancelled: z.boolean().nullish(),
    funds__fundraising_status__is_terminal: z.boolean().nullish(),
    funds__investment_status_id: z.string().nullish(),
    funds__investment_status__name: z.string().nullish(),
    funds__investment_status__is_open: z.boolean().nullish(),
    funds__investment_status__is_terminal: z.boolean().nullish(),

    // Drift HINT only — the dates never auto-write the status. Suppressed on a
    // cancelled fund, whose configured close date is meaningless.
    prog__suggested_fundraising_status_id: z.string().nullish(),
    prog__suggested_investment_status_id: z.string().nullish(),
    prog__fundraising_status_drift: z.boolean().nullish(),
    prog__investment_status_drift: z.boolean().nullish()
  })
  .strict()

export const FundTermsRecordFields = makeFieldKeys(FundTermsRecordSchema)

export type FundTermsRecord = z.infer<typeof FundTermsRecordSchema>

export function validateFundTermsRecord(data: unknown): FundTermsRecord {
  try {
    return validateWithZodSchema(FundTermsRecordSchema, data, 'fund terms record')
  } catch (error) {
    throw new Error(`Fund terms record validation failed: ${error}`)
  }
}
